import { ContactDTO } from '@/types';
import { Phone, Mail, Pencil, Trash2, Briefcase, StickyNote, ChevronRight } from 'lucide-react';
import clsx from 'clsx';
import { formatPhoneNumber } from '@/lib/phoneUtils';

export interface ContactListItem {
  id: string;
  contact: ContactDTO;
  notes?: string;
  meta?: string;
}

interface ContactListProps {
  contacts: ContactListItem[];
  onSelect?: (item: ContactListItem) => void;
  onEdit?: (item: ContactListItem) => void;
  onRemove?: (item: ContactListItem) => void;
  showActions?: boolean;
  selectable?: boolean;
  emptyMessage?: string;
  dense?: boolean;
}

// Soft avatar colors, picked from the name so each contact keeps the same one.
const AVATAR_COLORS = [
  'bg-blue-100 text-blue-700',
  'bg-indigo-100 text-indigo-700',
  'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-violet-100 text-violet-700',
];

const avatarFor = (name: string) => {
  const trimmed = (name || '').trim();
  const initials = trimmed
    ? trimmed.split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '').join('')
    : '?';
  let hash = 0;
  for (let i = 0; i < trimmed.length; i++) hash = (hash * 31 + trimmed.charCodeAt(i)) >>> 0;
  return { initials, color: AVATAR_COLORS[hash % AVATAR_COLORS.length] };
};

export function ContactList({
  contacts,
  onSelect,
  onEdit,
  onRemove,
  showActions = true,
  selectable = false,
  emptyMessage = 'No contacts found.',
  dense = false,
}: ContactListProps) {
  if (!contacts || contacts.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-white py-3 px-4 text-center text-sm text-gray-500">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className={clsx('space-y-2.5', dense && 'space-y-2')}>
      {contacts.map((item) => {
        const { contact, notes, meta } = item;
        const { initials, color } = avatarFor(contact.name);
        const jobCount = contact.jobs?.length;
        const hasActions = showActions && (onEdit || onRemove);
        const handleRowClick = () => {
          if (selectable && onSelect) {
            onSelect(item);
          }
        };

        return (
          <div
            key={item.id}
            className={clsx(
              'group flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-3 transition-all sm:p-4',
              selectable && 'cursor-pointer hover:border-blue-300 hover:shadow-md'
            )}
            onClick={handleRowClick}
          >
            <span className={clsx('flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold', color)}>
              {initials}
            </span>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate text-[15px] font-semibold text-gray-900">{contact.name || 'No contact name'}</span>
                {jobCount !== undefined && (
                  <span
                    className={clsx(
                      'inline-flex flex-shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold',
                      jobCount > 0 ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500'
                    )}
                  >
                    <Briefcase className="h-3 w-3" />
                    {jobCount} {jobCount === 1 ? 'job' : 'jobs'}
                  </span>
                )}
              </div>

              <div className="mt-1.5 flex flex-col gap-1 text-xs text-gray-600 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4">
                {contact.phone && (
                  <span className="flex min-w-0 items-center gap-1.5">
                    <Phone className="h-3.5 w-3.5 flex-shrink-0 text-green-600" />
                    <span className="truncate">
                      {formatPhoneNumber(contact.phone)}
                      {contact.additional_phone ? ` / ${formatPhoneNumber(contact.additional_phone)}` : ''}
                    </span>
                  </span>
                )}
                {contact.email && (
                  <span className="flex min-w-0 items-center gap-1.5">
                    <Mail className="h-3.5 w-3.5 flex-shrink-0 text-blue-500" />
                    <span className="truncate">{contact.email}</span>
                  </span>
                )}
                {meta && <span className="text-gray-400">{meta}</span>}
              </div>

              {notes && (
                <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
                  <StickyNote className="mt-px h-3.5 w-3.5 flex-shrink-0 text-amber-500" />
                  <span className="line-clamp-2">{notes}</span>
                </p>
              )}
            </div>

            <div className="flex flex-shrink-0 items-center gap-1 self-center">
              {hasActions && onEdit && (
                <button
                  type="button"
                  aria-label="Edit contact"
                  title="Edit"
                  onClick={(event) => {
                    event.stopPropagation();
                    onEdit(item);
                  }}
                  className="rounded-lg p-2 text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-700"
                >
                  <Pencil className="h-4 w-4" />
                </button>
              )}
              {hasActions && onRemove && (
                <button
                  type="button"
                  aria-label="Remove contact"
                  title="Remove"
                  onClick={(event) => {
                    event.stopPropagation();
                    onRemove(item);
                  }}
                  className="rounded-lg p-2 text-red-500 transition-colors hover:bg-red-50 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
              {selectable && (
                <ChevronRight className="hidden h-4 w-4 text-gray-300 transition-colors group-hover:text-blue-500 sm:block" />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
